import { computed, ref, shallowRef, watch } from 'vue'
import {
  createOrganizationFolder,
  createOrganizationSpace,
  deleteFolderProject,
  deleteOrganizationFolder,
  deleteOrganizationSpace,
  listOrganizationSpace,
  listOrganizationSubFolder,
  listOrganizationSubProject,
  listProjectGraphicalPreview,
  moveFolder,
  renameFolder,
  updateOrganizationFolder,
  updateOrganizationSpace,
} from '@elecmap/api/src/cad/space'
import {
  createProject,
  deleteProject,
  updateProject,
  renameProject,
  moveProject,
  copyProject,
  listUserProjectRole,
} from '@elecmap/api/src/cad/project'
import type {
  CadFolderShortVo,
  CadProjectShortVo,
  CreateFolderCommand,
  CreateSpaceCommand,
  MoveFolderCommand,
  RenameFolderCommand,
  UpdateFolderCommand,
  UpdateSpaceCommand,
} from '@elecmap/api/src/cad/space.d'
import type {
  CreateProjectCommand,
  MoveProjectCommand,
  UpdateProjectCommand,
} from '@elecmap/api/src/cad/project.d'
import { defineStore } from 'pinia'
import { message } from 'ant-design-vue'
import { t } from '@/i18n'
import { useRoute } from 'vue-router'
import { useStorageRef } from '@elecmap/shared/src/composable/use-storage-ref'
import { notificationSuccess } from '@elecmap/shared/src/utils/notification'
import userStore from './user'
import { listUserSpaceRole } from '@elecmap/api/src/cad/folder'

export const useSpaceStore = defineStore('spaceStore', () => {
  const route = useRoute()

  const orgSpaceList = ref<CadFolderShortVo[]>([])
  const orgFolderList = ref<CadFolderShortVo[]>([])
  const orgProjectList = ref<CadProjectShortVo[]>([])
  const currentSpace = ref<CadFolderShortVo>()
  const previewMap = ref<Record<number, string>>({})
  const frameMap = ref<Record<number, number>>({})

  const navs = useStorageRef<CadFolderShortVo[]>('workbench-navs', [])
  const currentFolder = computed(() => {
    if (navs.value.length) {
      return navs.value[navs.value.length - 1]
    }
    return null
  })

  const success = () => notificationSuccess(t('message.operatedSuccessfully'))

  async function listOrgSpace() {
    const d = await listOrganizationSpace()
    orgSpaceList.value = d
    listUserSpaceRole(orgSpaceList.value.map(r => r.id)).then(data => {
      data.forEach(roleObj => {
        const s = orgSpaceList.value.find(o => o.id === roleObj.folderId)
        if (s) {
          s.role = roleObj.memberPermission
          if (currentSpace.value?.id === s.id) {
            currentSpace.value.role = s.role
          }
        }
      })
    })
  }

  async function listOrgSubFolderProject() {
    if (currentFolder.value) {
      const [folders, projects] = await Promise.all([
        listOrganizationSubFolder(currentFolder.value.id),
        listOrganizationSubProject(currentFolder.value.id),
      ])
      orgFolderList.value = folders
      orgProjectList.value = projects ?? []
      if (projects?.length) {
        const ids = projects.map(item => item.id)
        listProjectGraphicalPreview(ids).then(previews => {
          previews.forEach(p => {
            previewMap.value[p.projectId] = p.graphicalPreview
            if (p.frameId) {
              frameMap.value[p.projectId] = p.frameId
            }
          })
        })
        listUserProjectRole(ids).then(data => {
          data.forEach(roleObj => {
            const s = orgProjectList.value.find(o => o.id === roleObj.projectId)
            if (s) {
              s.role = roleObj.memberPermission
            }
          })
        })
      }
    }
  }

  async function createOrgSpace(cmd: CreateSpaceCommand) {
    await createOrganizationSpace(cmd)
    success()
    listOrgSpace()
  }

  async function updateOrgSpace(cmd: UpdateSpaceCommand) {
    await updateOrganizationSpace(cmd)
    success()
    listOrgSpace()
  }

  async function deleteOrgSpace(id: number) {
    await deleteOrganizationSpace(id)
    success()
    listOrgSpace()
  }

  async function createOrgFolder(cmd: CreateFolderCommand) {
    await createOrganizationFolder(cmd)
    success()
    listOrgSubFolderProject()
  }

  async function updateOrgFolder(cmd: UpdateFolderCommand) {
    await updateOrganizationFolder(cmd)
    success()
    listOrgSubFolderProject()
  }

  async function renameOrgFolder(cmd: RenameFolderCommand) {
    await renameFolder(cmd)
    success()
    listOrgSubFolderProject()
  }

  async function deleteOrgFolder(id: number) {
    await deleteOrganizationFolder(id)
    success()
    listOrgSubFolderProject()
  }

  async function createOrgProject(data: CreateProjectCommand) {
    await createProject(data)
    success()
    listOrgSubFolderProject()
  }

  async function renameOrgProject(data: any) {
    await renameProject(data)
    success()
    listOrgSubFolderProject()
  }

  const copyModalVisible = ref(false)
  const copyModalText = ref('')
  async function copyOrgProject(data: any) {
    try {
      copyModalVisible.value = true
      copyModalText.value = data.name
      await copyProject({ projectId: data.id, name: data.name })
      success()
      listOrgSubFolderProject()
    } finally {
      copyModalVisible.value = false
    }
  }

  async function moveOrgFolder(data: MoveFolderCommand) {
    await moveFolder(data)
    success()
    listOrgSubFolderProject()
  }

  async function updateOrgProject(data: UpdateProjectCommand) {
    await updateProject(data)
    listOrgSubFolderProject()
  }

  async function deleteOrgProject(id: number) {
    await deleteProject(id)
    listOrgSubFolderProject()
  }

  async function moveOrgProject(cmd: MoveProjectCommand) {
    await moveProject(cmd)
    listOrgSubFolderProject()
  }

  function setCurrentFolder(folder: CadFolderShortVo, exist = false) {
    if (!exist) {
      navs.value.push(folder)
      return
    }
    const index = navs.value.findIndex(n => n.id === folder.id)
    if (index !== -1) {
      navs.value = navs.value.slice(0, index + 1)
    }
  }

  watch(
    () => currentSpace.value,
    cs => {
      if (!cs) return
      if (!navs.value.length) {
        navs.value = [cs]
      }
    },
  )

  watch([() => currentFolder.value, () => orgSpaceList.value], () => {
    if (!orgSpaceList.value.length || !userStore().userInfo.organizationId) return
    const navIds = navs.value.map(n => n.id)
    // 缓存值在当前空间里才调接口
    if (orgSpaceList.value.some(n => navIds.includes(n.id))) {
      listOrgSubFolderProject()
    }
  })

  function init() {
    listOrgSpace().then(() => {
      if (route?.path === '/workbench/index') {
        const index = orgSpaceList.value.findIndex(o => o.id === navs.value[0]?.id)
        currentSpace.value = index === -1 ? orgSpaceList.value[0] : orgSpaceList.value[index]
        if (index === -1) {
          navs.value = [currentSpace.value]
        }
      }
    })
  }

  return {
    orgSpaceList,
    currentSpace,
    orgFolderList,
    previewMap,
    frameMap,
    orgProjectList,
    navs,
    currentFolder,
    copyModalVisible,
    copyModalText,

    listOrgSpace,
    listOrgSubFolderProject,
    init,
    createOrgSpace,
    updateOrgSpace,
    deleteOrgSpace,
    createOrgFolder,
    updateOrgFolder,
    renameOrgFolder,
    renameOrgProject,
    copyOrgProject,
    moveOrgFolder,
    moveOrgProject,
    deleteOrgFolder,
    createOrgProject,
    deleteOrgProject,
    updateOrgProject,
    setCurrentFolder,
  }
})
